import { Network } from '@capacitor/network';

import { isStoreBuild } from './platform';

/**
 * Состояние сети — один источник правды на всё приложение.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ, А НЕ `navigator.onLine`. Браузерный флаг отвечает лишь
 * на вопрос «есть ли вообще сетевой интерфейс»: в вагоне метро с подключённым, но
 * мёртвым Wi-Fi он бодро говорит «онлайн». В приложении спрашиваем систему через
 * плагин — она знает и тип подключения, и его реальное наличие. В браузере
 * остаётся `navigator.onLine`: другого там нет.
 *
 * Модуль хранит текущее значение синхронно, потому что его спрашивают из мест,
 * где ждать нельзя, — например, из очереди отправки перед попыткой послать ответ.
 */

type Listener = (online: boolean) => void;

let online = true;
const listeners = new Set<Listener>();

function set(next: boolean): void {
  if (next === online) return;
  online = next;
  listeners.forEach((l) => {
    try {
      l(next);
    } catch {
      /* один сломавшийся слушатель не должен мешать остальным */
    }
  });
}

export function isOnline(): boolean {
  return online;
}

export function onNetworkChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Запускается один раз при старте приложения. Возвращает функцию отписки —
 * нужна в режиме разработки, где модуль перезагружается на лету.
 */
export function initNetwork(): () => void {
  if (isStoreBuild()) {
    let remove: (() => void) | undefined;
    void Network.getStatus().then((s) => set(s.connected));
    void Network.addListener('networkStatusChange', (s) => set(s.connected)).then((h) => {
      remove = () => void h.remove();
    });
    return () => remove?.();
  }

  // Браузер: событий два, отдельных для появления и пропажи связи.
  const up = () => set(true);
  const down = () => set(false);
  set(navigator.onLine);
  window.addEventListener('online', up);
  window.addEventListener('offline', down);
  return () => {
    window.removeEventListener('online', up);
    window.removeEventListener('offline', down);
  };
}
